import { useState, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { chartApi, type ChartDataPoint } from '../../lib/api-client';
import { calculateTrendline, calculateLinearTrendline } from '../../lib/charts/trendCalculations';
import {
  commonChartOptions,
  getCommonXAxis,
  getVolumeYAxis,
  getRateYAxis,
  getCommonTooltip,
  createTrendlineSeries,
  createUpperBandSeries,
  createLowerBandSeries,
  linePlotOptions,
  columnPlotOptions,
  areaPlotOptions,
  generateCorrectMonthLabels,
} from '../../lib/charts/chartConfig';
import TrendChartViewToggle, { type TrendChartView } from './TrendChartViewToggle';

// Component colors
const COMPONENT_COLORS = {
  RBC: '#b32017',
  Plasma: '#f59e0b',
  Platelets: '#00467f',
};

interface MonthlyTransfusionTrendsProps {
  startDate?: string;
  endDate?: string;
  year?: number;
}

export default function MonthlyTransfusionTrends({
  startDate,
  endDate,
  year,
}: MonthlyTransfusionTrendsProps) {
  const [data, setData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<TrendChartView>('dual-axis');

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        const response = await chartApi.getTemporalData({ startDate, endDate, year });
        if (response.success) {
          setData(response.data);
        } else {
          setError(response.error || 'Failed to fetch data');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch chart data');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [startDate, endDate, year]);

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
        <div className="text-gray-500">Loading chart data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
        <div className="text-gray-500">No data available for this date range</div>
        <p className="text-sm text-gray-400 mt-2">Try Prior Fiscal Year or another range.</p>
      </div>
    );
  }

  const rbcValues = data.map((d) => d.rbc || 0);
  const plasmaValues = data.map((d) => d.plasma || 0);
  const plateletsValues = data.map((d) => d.platelets || 0);
  const transfusedRateValues = data.map((d) => d.transfusedRate || 0);

  const rbcTrend = calculateTrendline(rbcValues);
  const plasmaTrend = calculateTrendline(plasmaValues);
  const plateletsTrend = calculateTrendline(plateletsValues);

  const categories = generateCorrectMonthLabels(data);
  const totalTransfused = data.reduce((sum, d) => sum + d.value, 0);

  const renderTable = () => (
    <div className="overflow-x-auto h-full rounded-b-lg">
      <table className="w-full h-full divide-y divide-gray-200 rounded-b-lg overflow-hidden">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Month
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              RBC
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Plasma
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Platelets
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Total Transfused
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Total Units
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Transfused Rate
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((month, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="px-6 py-4 text-sm font-medium text-gray-900">{month.label}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                {(month.rbc || 0).toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                {(month.plasma || 0).toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                {(month.platelets || 0).toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                {month.value.toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                {(month.total || 0).toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                {(month.transfusedRate || 0).toFixed(1)}%
              </td>
            </tr>
          ))}
          <tr className="bg-gray-50 border-t-2 border-gray-300">
            <td className="px-6 py-4 text-sm font-bold text-gray-900">Total</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
              {rbcValues.reduce((a, b) => a + b, 0).toLocaleString()}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
              {plasmaValues.reduce((a, b) => a + b, 0).toLocaleString()}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
              {plateletsValues.reduce((a, b) => a + b, 0).toLocaleString()}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
              {totalTransfused.toLocaleString()}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
              {data.reduce((sum, d) => sum + (d.total || 0), 0).toLocaleString()}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
              {data.reduce((sum, d) => sum + (d.total || 0), 0) > 0
                ? ((totalTransfused / data.reduce((sum, d) => sum + (d.total || 0), 0)) * 100).toFixed(1)
                : '0.0'}
              %
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const renderLineChart = () => {
    const totalTransfusedValues = rbcValues.map((rbc, i) => rbc + plasmaValues[i] + plateletsValues[i]);
    const totalLinearTrend = calculateLinearTrendline(totalTransfusedValues);

    const series: any[] = [
      {
        name: 'RBC',
        data: rbcValues,
        type: 'line',
        color: COMPONENT_COLORS.RBC,
        lineWidth: 3,
        marker: { enabled: true, radius: 4, fillColor: COMPONENT_COLORS.RBC, lineColor: '#ffffff', lineWidth: 2 },
      },
      {
        name: 'Plasma',
        data: plasmaValues,
        type: 'line',
        color: COMPONENT_COLORS.Plasma,
        lineWidth: 3,
        marker: { enabled: true, radius: 4, fillColor: COMPONENT_COLORS.Plasma, lineColor: '#ffffff', lineWidth: 2 },
      },
      {
        name: 'Platelets',
        data: plateletsValues,
        type: 'line',
        color: COMPONENT_COLORS.Platelets,
        lineWidth: 3,
        marker: { enabled: true, radius: 4, fillColor: COMPONENT_COLORS.Platelets, lineColor: '#ffffff', lineWidth: 2 },
      },
    ];

    ['RBC', 'Plasma', 'Platelets'].forEach((component, idx) => {
      const trend = idx === 0 ? rbcTrend : idx === 1 ? plasmaTrend : plateletsTrend;
      const color = COMPONENT_COLORS[component as keyof typeof COMPONENT_COLORS];
      series.push(createUpperBandSeries(trend, color), createLowerBandSeries(trend, color), createTrendlineSeries(trend, color));
    });

    series.push({
      ...createTrendlineSeries(totalLinearTrend, '#6b7280', 3),
      dashStyle: 'Dash',
      lineWidth: 3,
    });

    const options: Highcharts.Options = {
      chart: { type: 'line', backgroundColor: 'transparent', height: 350, spacing: [20, 20, 20, 20] },
      title: { text: undefined },
      xAxis: {
        categories,
        labels: { rotation: -45, style: { fontSize: '11px', color: '#6b7280', fontWeight: '500' } },
        lineColor: '#e5e7eb',
        tickColor: '#e5e7eb',
      },
      yAxis: {
        title: { text: undefined },
        labels: {
          style: { fontSize: '11px', color: '#6b7280', fontWeight: '500' },
          formatter: function () {
            const val = typeof this.value === 'number' ? this.value : 0;
            return val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val.toString();
          },
        },
        gridLineColor: '#f1f5f9',
      },
      tooltip: {
        ...getCommonTooltip((pointIndex) => data[pointIndex]?.label || categories[pointIndex] || '').tooltip,
        formatter: function (this: any) {
          const pointIndex = this.points?.[0]?.point?.index ?? this.points?.[0]?.x ?? 0;
          const monthName = data[pointIndex]?.label || categories[pointIndex] || this.x;
          let tooltip = `<b>${monthName}</b><br/>`;
          let total = 0;
          this.points?.forEach((point: any) => {
            // Only show actual data series, not trendlines
            if (point.series.type === 'line' && !point.series.name.includes('Trend') && !point.series.name.includes('Band')) {
              tooltip += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${point.y?.toLocaleString()}</b><br/>`;
              total += point.y || 0;
            }
          });
          if (total > 0) {
            tooltip += `<br/><b>Total: ${total.toLocaleString()}</b>`;
          }
          return tooltip;
        },
      },
      legend: { enabled: true, align: 'center', verticalAlign: 'bottom' },
      plotOptions: {
        line: linePlotOptions,
      },
      series,
      credits: { enabled: false },
    };

    return (
      <div className="p-6">
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    );
  };

  const renderStackedAreaChart = () => {
    const totalTransfusedValues = rbcValues.map((rbc, i) => rbc + plasmaValues[i] + plateletsValues[i]);
    const totalTransfusedTrend = calculateLinearTrendline(totalTransfusedValues);

    const series: any[] = [
      {
        name: 'RBC',
        data: rbcValues,
        type: 'area',
        color: COMPONENT_COLORS.RBC,
        fillOpacity: 0.7,
        lineWidth: 2,
        marker: { enabled: false },
      },
      {
        name: 'Plasma',
        data: plasmaValues,
        type: 'area',
        color: COMPONENT_COLORS.Plasma,
        fillOpacity: 0.7,
        lineWidth: 2,
        marker: { enabled: false },
      },
      {
        name: 'Platelets',
        data: plateletsValues,
        type: 'area',
        color: COMPONENT_COLORS.Platelets,
        fillOpacity: 0.7,
        lineWidth: 2,
        marker: { enabled: false },
      },
    ];

    series.push(createTrendlineSeries(totalTransfusedTrend, '#00467f', 2));

    const options: Highcharts.Options = {
      chart: { type: 'area', backgroundColor: 'transparent', height: 350, spacing: [20, 20, 20, 20] },
      title: { text: undefined },
      xAxis: {
        categories,
        labels: { rotation: -45, style: { fontSize: '11px', color: '#6b7280', fontWeight: '500' } },
        lineColor: '#e5e7eb',
        tickColor: '#e5e7eb',
      },
      yAxis: {
        title: { text: undefined },
        labels: {
          style: { fontSize: '11px', color: '#6b7280', fontWeight: '500' },
          formatter: function () {
            const val = typeof this.value === 'number' ? this.value : 0;
            return val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val.toString();
          },
        },
        gridLineColor: '#f1f5f9',
      },
      tooltip: {
        ...getCommonTooltip((pointIndex) => data[pointIndex]?.label || '').tooltip,
        formatter: function (this: any) {
          const pointIndex = this.points?.[0]?.point?.index ?? this.points?.[0]?.x ?? 0;
          const monthName = data[pointIndex]?.label || this.x;
          let tooltip = `<b>${monthName}</b><br/>`;
          this.points?.forEach((point: any) => {
            tooltip += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${point.y?.toLocaleString()}</b><br/>`;
          });
          const total = this.points?.reduce((sum: number, point: any) => sum + (point.y || 0), 0) || 0;
          tooltip += `<br/><b>Total: ${total.toLocaleString()}</b>`;
          return tooltip;
        },
      },
      legend: { enabled: true, align: 'center', verticalAlign: 'bottom' },
      plotOptions: {
        area: areaPlotOptions,
      },
      series,
      credits: { enabled: false },
    };

    return (
      <div className="p-6">
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    );
  };

  const renderDualAxisChart = () => {
    const totalTransfusedValues = rbcValues.map((rbc, i) => rbc + plasmaValues[i] + plateletsValues[i]);
    const totalTransfusedTrend = calculateLinearTrendline(totalTransfusedValues);

    const series: any[] = [
      {
        name: 'RBC',
        type: 'column',
        yAxis: 0,
        data: rbcValues,
        color: COMPONENT_COLORS.RBC,
        stacking: 'normal',
      },
      {
        name: 'Plasma',
        type: 'column',
        yAxis: 0,
        data: plasmaValues,
        color: COMPONENT_COLORS.Plasma,
        stacking: 'normal',
      },
      {
        name: 'Platelets',
        type: 'column',
        yAxis: 0,
        data: plateletsValues,
        color: COMPONENT_COLORS.Platelets,
        stacking: 'normal',
      },
      {
        name: 'Transfused Rate',
        type: 'line',
        yAxis: 1,
        data: transfusedRateValues,
        color: '#7c3aed',
        lineWidth: 3,
        marker: {
          enabled: true,
          radius: 4,
          fillColor: '#7c3aed',
          lineColor: '#ffffff',
          lineWidth: 2,
        },
      },
    ];

    // Add linear trendline for total units
    series.push(
      createTrendlineSeries(totalTransfusedTrend, '#00467f', 2)
    );

    const options: Highcharts.Options = {
      chart: { type: 'line', ...commonChartOptions },
      title: { text: undefined },
      xAxis: getCommonXAxis(categories),
      yAxis: [
        getVolumeYAxis('Units Transfused'),
        getRateYAxis('Transfused Rate (%)'),
      ],
      tooltip: {
        ...getCommonTooltip((pointIndex) => data[pointIndex]?.label || '').tooltip,
        formatter: function (this: any) {
          const pointIndex = this.points?.[0]?.point?.index ?? this.points?.[0]?.x ?? 0;
          const monthName = data[pointIndex]?.label || this.x;
          let tooltip = `<b>${monthName}</b><br/>`;
          let total = 0;
          this.points?.forEach((point: any) => {
            if (point.series.type === 'column') {
              tooltip += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${point.y?.toLocaleString()}</b><br/>`;
              total += point.y || 0;
            } else if (point.series.name === 'Transfused Rate') {
              tooltip += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${point.y?.toFixed(1)}%</b><br/>`;
            }
          });
          if (total > 0) {
            tooltip += `<br/><b>Total: ${total.toLocaleString()}</b>`;
          }
          return tooltip;
        },
      },
      legend: { enabled: true, align: 'center', verticalAlign: 'bottom' },
      plotOptions: {
        column: {
          ...columnPlotOptions,
          stacking: 'normal',
        },
        line: {
          ...linePlotOptions,
          color: '#7c3aed', // Purple color for transfused rate
        },
      },
      series,
      credits: { enabled: false },
    };

    return (
      <div className="p-6">
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg break-inside-avoid mb-6">
      <div className="relative border-b border-gray-200">
        <TrendChartViewToggle activeView={activeView} onViewChange={setActiveView} />
        <div className="px-6 pb-4" style={{ paddingTop: '3rem', paddingRight: '8rem' }}>
          <h3 className="text-lg font-semibold text-gray-900">Monthly Transfusion Trends</h3>
          <p className="text-sm text-gray-600 mt-1">Track transfused units and rates over time</p>
        </div>
      </div>
      <div>
        {activeView === 'table' && renderTable()}
        {activeView === 'dual-axis' && renderDualAxisChart()}
        {activeView === 'line' && renderLineChart()}
        {activeView === 'stacked-area' && renderStackedAreaChart()}
      </div>
      {activeView !== 'table' && totalTransfused > 0 && (
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-sm font-medium text-gray-700">Total Transfused:</span>
              <span className="text-sm font-bold text-gray-900 ml-2">{totalTransfused.toLocaleString()}</span>
            </div>
            <div className="text-right">
              <span className="text-sm font-medium text-gray-700">Overall Rate:</span>
              <span className="text-sm font-bold text-gray-900 ml-2">
                {data.reduce((sum, d) => sum + (d.total || 0), 0) > 0
                  ? ((totalTransfused / data.reduce((sum, d) => sum + (d.total || 0), 0)) * 100).toFixed(1)
                  : '0.0'}
                %
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
